import { InsightsActionsRequest } from '../api/types/insights-actions-request';
import { PeriodTypesEnum } from '../enums/period-types-enum';
import { timestampToMMDDYYYY } from '../utilities';

/**
 * The date/period slice of an InsightsActionsRequest's metadata. Kept as its own
 * type so callers can hand over an already-formatted period (see toInsightsPeriod).
 */
export type InsightsActionsPeriod = {
	time_period: string;
	time_range: {
		start_date: string;
		end_date: string;
	};
};

export type InsightsActionsLogging = InsightsActionsRequest['logging'];

export type BuildInsightsActionsRequestArgs = {
	/** Key measure titles, in the order they should be described to the model. */
	keyMeasures: string[];
	/**
	 * Dimension titles. For a pivot table this must include BOTH the row and the
	 * column dimensions - the column dimensions are invisible to a rowEntry-only list.
	 */
	dimensions: string[];
	/** The `columns` block straight off the data retrieval response json. */
	columns: string[][];
	period: InsightsActionsPeriod;
	/** Human readable "Title: value" strings, excluding the date period selector. */
	filtersApplied: string[];
	extendedPrompt?: string;
	/** Raw (unformatted) CSV payload from the data retrieval response. */
	csv?: string;
	/** Maximum number of CSV *data* rows to send. The header row is always kept. */
	maxRows?: number;
	logging: InsightsActionsLogging;
};

/**
 * Charts cap their retrieval at 500 rows; tables do not, so the payload has to be
 * capped here instead or a large table would blow past the lambda's prompt budget.
 */
export const DEFAULT_INSIGHTS_MAX_ROWS = 500;

/**
 * Keep the header row plus at most `maxRows` data rows of a CSV payload.
 *
 * A CSV that is already within the limit is returned byte-identical, so a caller
 * that never exceeds the cap sends exactly what the API returned. A trailing
 * newline is preserved rather than being counted as a data row.
 */
export const truncateCsvRows = (csv: string, maxRows = DEFAULT_INSIGHTS_MAX_ROWS): string => {
	if (!csv || maxRows < 0) {
		return csv;
	}

	const lines = csv.split('\n');

	const hasTrailingNewline = lines.length > 1 && lines[lines.length - 1] === '';
	if (hasTrailingNewline) {
		lines.pop();
	}

	// lines[0] is the header, so the data row count is one less than the line count.
	if (lines.length - 1 <= maxRows) {
		return csv;
	}

	return lines.slice(0, maxRows + 1).join('\n') + (hasTrailingNewline ? '\n' : '');
};

/**
 * Turn a date range + period id into the `time_period` / `time_range` metadata the
 * insights lambda expects. Mirrors the getPeriodData() copies that already live in
 * BuildChart, ChartView and DashboardView.
 */
export const toInsightsPeriod = (date?: {
	begin_date: number;
	end_date: number;
	period: number;
}): InsightsActionsPeriod => {
	const period: InsightsActionsPeriod = {
		time_period: '',
		time_range: {
			start_date: '',
			end_date: '',
		},
	};

	if (!date) {
		return period;
	}

	period.time_range.start_date = timestampToMMDDYYYY(date.begin_date);
	period.time_range.end_date = timestampToMMDDYYYY(date.end_date);

	switch (date.period) {
		case 1: {
			period.time_period = PeriodTypesEnum.Year;
			break;
		}
		case 2: {
			period.time_period = PeriodTypesEnum.Quarter;
			break;
		}
		case 3: {
			period.time_period = PeriodTypesEnum.Month;
			break;
		}
		case 4: {
			period.time_period = PeriodTypesEnum.Week;
			break;
		}
		default: {
			period.time_period = PeriodTypesEnum.Month;
			break;
		}
	}

	return period;
};

/**
 * Assemble the request posted to `app/ai/insights-actions`.
 *
 * Pure - it reads nothing but its arguments, so the shape of the payload can be
 * unit tested without standing up a data builder.
 */
export const buildInsightsActionsRequest = (
	args: BuildInsightsActionsRequestArgs
): InsightsActionsRequest => {
	return {
		metadata: {
			key_measures: args.keyMeasures.map((name) => ({ name, description: '' })),
			dimensions: args.dimensions.map((name) => ({ name, description: '' })),
			columns: args.columns,
			time_period: args.period.time_period,
			time_range: {
				start_date: args.period.time_range.start_date,
				end_date: args.period.time_range.end_date,
			},
			filters_applied: args.filtersApplied,
			extended_prompt: args.extendedPrompt,
		},
		data: args.csv ? truncateCsvRows(args.csv, args.maxRows) : '',
		logging: { ...args.logging },
	};
};
